/**
 * NotesModal. A SHARED, data-source-agnostic centered notes modal (COCKPIT.md
 * section 2.10). A backdrop plus a centered card that opens IN PLACE as an
 * overlay: it never routes or navigates. Backdrop click and Escape close it.
 *
 * This is the look Steven prefers: notes and an optional audit trail in a
 * centered pop-up, not an anchored popover. The component is presentational.
 * The host passes the already-loaded notes (and optionally an audit list) plus
 * an onAddNote callback and loading/error props. Both Commerce Orders and
 * Finance Invoices consume it via thin wrappers (OrderNotesPopover /
 * InvoiceNotesPopover), each feeding its own tables:
 *   - Orders: commerce_order_notes + the order audit feed (audit section shown).
 *   - Invoices: invoice_notes + the invoice audit feed (audit section shown).
 *
 * The audit-trail section renders only when an `audit` array is provided, so a
 * surface with no audit feed simply omits the section. Styling uses the shared
 * amp-* tokens per section 2.9; no hand-typed colors.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from './Modal';

// A single note, normalized to what the modal renders. The host maps its own
// note shape onto this before passing the array in.
export type NoteEntry = {
  id: number | string;
  // Who wrote the note (email or display name). May be null for system notes.
  author: string | null;
  body: string;
  // ISO timestamp; rendered via the formatTimestamp prop so the host controls
  // the date format (e.g. dateUtils.formatDateTime).
  created_at: string;
};

// A single audit-trail event. Optional section: only rendered when the host
// passes an `audit` array. `detail` is free-form (string or object) and shown
// as preformatted text when present.
export type AuditEntry = {
  id: number | string;
  // Who performed the action (email or display name). May be null for system.
  actor: string | null;
  // Machine action name (e.g. "ship", "match", "note_added").
  action: string;
  // Optional outcome / status word shown next to the actor.
  outcome?: string | null;
  // Optional free-form detail blob.
  detail?: unknown;
  // ISO timestamp; rendered via formatTimestamp.
  occurred_at: string;
};

type Tab = 'notes' | 'audit';

type NotesModalProps = {
  // Heading shown in the modal header (e.g. the order number or invoice label).
  title: string;
  // Optional eyebrow caption above the title (e.g. "Order", "Invoice").
  eyebrow?: string;
  // Notes to list, newest first.
  notes: NoteEntry[];
  // Whether the notes list is loading.
  notesLoading?: boolean;
  // Error message in place of the notes list, if the load failed.
  notesError?: string | null;
  // Optional audit trail. When provided, an "Audit trail" tab is shown. When
  // omitted, the modal shows notes only (no tabs).
  audit?: AuditEntry[];
  // Whether the audit list is loading (only relevant when audit is provided).
  auditLoading?: boolean;
  // Error message in place of the audit list, if the load failed.
  auditError?: string | null;
  // Whether the current user can add a note. When false, the input is hidden.
  canWrite?: boolean;
  // Called with the trimmed note body when the user submits a new note. The
  // host owns the mutation.
  onAddNote?: (body: string) => void;
  // Whether an add is in flight (disables the input + button).
  adding?: boolean;
  // Add error message, shown under the input.
  addError?: string | null;
  // Formats an ISO timestamp for display. Defaults to the raw string.
  formatTimestamp?: (iso: string) => string;
  // Closes the modal. Wired to the backdrop, the X button, and Escape.
  onClose: () => void;
};

export function NotesModal({
  title,
  eyebrow,
  notes,
  notesLoading = false,
  notesError = null,
  audit,
  auditLoading = false,
  auditError = null,
  canWrite = false,
  onAddNote,
  adding = false,
  addError = null,
  formatTimestamp = (iso) => iso,
  onClose,
}: NotesModalProps) {
  const hasAudit = audit !== undefined;
  const [tab, setTab] = useState<Tab>('notes');
  const [draft, setDraft] = useState('');

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    onAddNote?.(text);
    setDraft('');
  };

  return (
    <Modal title={title} eyebrow={eyebrow} onClose={onClose}>
        {hasAudit && (
          <div className="flex gap-1 border-b border-amp-line px-5 dark:border-amp-dark-line">
            <TabButton active={tab === 'notes'} onClick={() => setTab('notes')}>
              Notes
            </TabButton>
            <TabButton active={tab === 'audit'} onClick={() => setTab('audit')}>
              Audit trail
            </TabButton>
          </div>
        )}

        {tab === 'notes' || !hasAudit ? (
          <div className="p-5">
            {canWrite && (
              <div className="mb-4">
                {/* Stays a plain box. Note bodies are rendered as plain text just below,
                    and nothing sanitizes HTML on the order and invoice note endpoints
                    yet. Backend tracked in api#563, not approved. */}
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={3}
                  placeholder="Add a note..."
                  aria-label="Add a note"
                  className="w-full rounded border border-amp-line bg-amp-white px-3 py-2 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
                />
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setDraft('')}
                    disabled={!draft || adding}
                    className="rounded border border-amp-line px-3 py-1 text-xs text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={!draft.trim() || adding}
                    className="inline-flex items-center gap-1 rounded border border-amp-ink bg-amp-ink px-3 py-1 text-xs text-amp-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
                  >
                    {adding && <Loader2 className="h-3 w-3 animate-spin" aria-hidden />}
                    Save note
                  </button>
                </div>
                {addError && (
                  <p className="mt-2 text-xs text-red-700 dark:text-red-300">{addError}</p>
                )}
              </div>
            )}
            <div className="max-h-[50vh] space-y-3 overflow-y-auto">
              {notesLoading ? (
                <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Loading...
                </p>
              ) : notesError ? (
                <p className="text-sm text-red-700 dark:text-red-300">{notesError}</p>
              ) : notes.length === 0 ? (
                <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  No notes yet.
                </p>
              ) : (
                notes.map((n) => (
                  <div
                    key={n.id}
                    className="rounded border border-amp-line p-3 text-sm dark:border-amp-dark-line"
                  >
                    <div className="mb-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                      <span className="font-mono">{n.author ?? 'system'}</span>
                      <span className="ml-2">{formatTimestamp(n.created_at)}</span>
                    </div>
                    <p className="whitespace-pre-wrap break-words text-amp-ink dark:text-amp-dark-ink">
                      {n.body}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="p-5">
            <div className="max-h-[60vh] space-y-2 overflow-y-auto">
              {auditLoading ? (
                <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Loading...
                </p>
              ) : auditError ? (
                <p className="text-sm text-red-700 dark:text-red-300">{auditError}</p>
              ) : (audit?.length ?? 0) === 0 ? (
                <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  No audit events yet.
                </p>
              ) : (
                audit!.map((e) => (
                  <div
                    key={e.id}
                    className="rounded border border-amp-line p-3 text-sm dark:border-amp-dark-line"
                  >
                    <div className="mb-1 flex items-center justify-between text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                      <span className="font-mono">{e.action}</span>
                      <span>{formatTimestamp(e.occurred_at)}</span>
                    </div>
                    <div className="text-xs">
                      <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
                        {e.actor ?? 'system'}
                        {e.outcome ? ` - ${e.outcome}` : ''}
                      </span>
                    </div>
                    {e.detail ? (
                      <pre className="mt-2 max-h-32 overflow-auto font-mono text-[11px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
                        {typeof e.detail === 'string'
                          ? e.detail
                          : JSON.stringify(e.detail, null, 2)}
                      </pre>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
    </Modal>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium ${
        active
          ? 'border-b-2 border-amp-olive text-amp-ink dark:border-amp-olive-dark dark:text-amp-dark-ink'
          : 'text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink'
      }`}
    >
      {children}
    </button>
  );
}
